import { FC } from 'react';
import styled from '@emotion/styled';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import { AlertDialog } from '../../../molecules/dialogs-common/alert-popup';
import { LoadingScreen } from '../../../molecules/loading-screen/loading-screen';
import { Typography } from '../../../atoms/progress-with-label/progress-with-label';

interface MySchoolTemplateProps {
  iframeUrl: string;
  isLoading: boolean;
  hasError: boolean;
  isConnected: boolean;
  showNoInternetPopup: boolean;
  onIframeLoad: () => void;
  onIframeError: () => void;
  onNoInternetPopupClose: () => void;
}

export const MySchoolTemplate: FC<MySchoolTemplateProps> = ({
  iframeUrl,
  isLoading,
  hasError,
  isConnected,
  showNoInternetPopup,
  onIframeLoad,
  onIframeError,
  onNoInternetPopupClose,
}) => {
  const renderContent = () => {
    if (hasError) {
      return (
        <ErrorContainer>
          <Typography
            data-cy="myschool-error-title"
            variant="textHeadlineMediumOnSurface"
          >
            Failed to Load My School
          </Typography>
          <Typography variant="textBodyMediumOnSurfaceVariant">
            There was an error loading My School. Please try again.
          </Typography>
        </ErrorContainer>
      );
    }

    if (isConnected) {
      return (
        <ContentWrapper>
          {isLoading && <LoadingScreen />}
          <MySchoolContainer isVisible={!isLoading}>
            <MySchoolIframe
              data-cy="myschool-iframe"
              src={iframeUrl}
              width="100%"
              height="100%"
              title="My School"
              onLoad={onIframeLoad}
              onError={onIframeError}
            />
          </MySchoolContainer>
        </ContentWrapper>
      );
    }
  };

  return (
    <>
      {renderContent()}

      <AlertDialog
        data-cy="myschool-no-internet-dialog"
        title="No Internet Connection"
        description="Seems like your internet is not connected. Connect and try again."
        showPopup={showNoInternetPopup}
        onClosePopup={onNoInternetPopupClose}
      />
    </>
  );
};

const ContentWrapper = styled.div`
  position: relative;
  width: 100%;
  height: calc(100vh - 180px);

  ${mq(BreakpointSizes.xs)} {
    height: calc(100vh - 160px);
  }
`;

const MySchoolContainer = styled.div<{ isVisible: boolean }>`
  width: 100%;
  border-radius: var(--md-sys-shape-corner-large);
  overflow: hidden;
  box-shadow: var(--md-sys-elevation-level1);
  background-color: var(--md-sys-color-surface);
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transition: opacity 0.3s ease-in-out;
  position: ${(props) => (props.isVisible ? 'static' : 'absolute')};
  top: 0;
  left: 0;
`;

const MySchoolIframe = styled.iframe`
  border: none;
  display: block;
  border-radius: var(--md-sys-shape-corner-large);
  width: 100%;
  height: calc(100vh - 20px);
  ${mq(BreakpointSizes.xs)} {
    height: calc(100vh - 100px);
  }

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: calc(100vh - 180px);
  text-align: center;
  gap: var(--rk-spacing-md);
  background-color: var(--md-sys-color-surface);
  border-radius: var(--md-sys-shape-corner-large);
  padding: var(--rk-spacing-xl);

  ${mq(BreakpointSizes.xs)} {
    height: calc(100vh - 160px);
  }
`;
